/**
 * Partner API client. Mirrors adminApi.js pattern.
 * Supports session token (magic-link) and API key auth.
 */
import { getBackendUrl } from '../config/api';

const PARTNER_SESSION_KEY = 'cv_partner_session_token';
const PARTNER_API_KEY_KEY = 'cv_partner_api_key';

function getPartnerApiBase() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    const isLocalFrontend = origin.includes('localhost:3000') || origin.includes('127.0.0.1:3000');
    const backend = getBackendUrl();
    if (isLocalFrontend && backend) {
      return backend.replace(/\/$/, '');
    }
    // Always use same-origin in deployed environments so partner cookie auth works
    // from both /partner and /dashboard routes.
    return origin;
  }
  return getBackendUrl();
}

export function getPartnerAuthBase() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    const isLocalFrontend = origin.includes('localhost:3000') || origin.includes('127.0.0.1:3000');
    const backend = getBackendUrl();
    if (isLocalFrontend && backend) {
      return backend.replace(/\/$/, '');
    }
    return origin;
  }
  return getBackendUrl() || '';
}

export function getPartnerToken() {
  return sessionStorage.getItem(PARTNER_SESSION_KEY);
}

export function setPartnerToken(token) {
  if (token) sessionStorage.setItem(PARTNER_SESSION_KEY, token);
  else sessionStorage.removeItem(PARTNER_SESSION_KEY);
}

export function getPartnerApiKey() {
  return localStorage.getItem(PARTNER_API_KEY_KEY);
}

export function setPartnerApiKey(key) {
  if (key) localStorage.setItem(PARTNER_API_KEY_KEY, key);
  else localStorage.removeItem(PARTNER_API_KEY_KEY);
}

export function partnerFetch(path, options = {}) {
  const base = getPartnerApiBase();
  if (!base) return Promise.reject(new Error('Backend URL not set'));
  const token = getPartnerToken();
  const apiKey = getPartnerApiKey();
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (apiKey) headers['X-Partner-Api-Key'] = apiKey;
  return fetch(`${base}${path}`, { ...options, headers });
}

export async function partnerGet(path) {
  const res = await partnerFetch(path);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, ...data };
  return data;
}

export async function partnerPost(path, body) {
  const res = await partnerFetch(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, ...data };
  return data;
}

export async function partnerPatch(path, body) {
  const res = await partnerFetch(path, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, ...data };
  return data;
}

export async function partnerDelete(path) {
  const res = await partnerFetch(path, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, ...data };
  return data;
}
